/**
 * Severity vocabulary and labelling for the incidents filter.
 *
 * Lives beside `Incidents.tsx` rather than inside it so these stay unit
 * testable without rendering the page (and so the page file keeps exporting
 * only its component, which is what react-refresh wants).
 */

import type { IncidentSeverity } from '@/hooks/useIncidents';

/** Most severe first — the order the checkbox list and trigger label use. */
export const ALL_SEVERITIES: readonly IncidentSeverity[] = ['critical', 'high', 'medium', 'low', 'info'] as const;

/**
 * Everything except `info`. Info-severity incidents are notifications —
 * audit findings and the like — and they outnumber real incidents enough to
 * bury them, which is why the page has always hidden them by default. The
 * difference now is that the box is visibly unticked and carries its count.
 */
export const DEFAULT_SEVERITIES: IncidentSeverity[] = ['critical', 'high', 'medium', 'low'];

export const SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  info: 'Info',
};

/**
 * Name the live selection. Never "All severities" unless it really is all —
 * the whole bug this replaces was a control that claimed to show everything
 * while suppressing a severity.
 */
export function severitySummary(selected: IncidentSeverity[]): string {
  // Unreachable through the UI — the last checkbox is disabled, because an
  // empty set sends no severity param and the server would answer with
  // everything. Kept so the label can never silently read "All severities".
  if (selected.length === 0) return 'No severities';
  if (selected.length === ALL_SEVERITIES.length) return 'All severities';

  const missing = ALL_SEVERITIES.filter((s) => !selected.includes(s));
  if (missing.length === 1) return `All except ${SEVERITY_LABELS[missing[0]]}`;
  if (selected.length <= 2) {
    return ALL_SEVERITIES.filter((s) => selected.includes(s))
      .map((s) => SEVERITY_LABELS[s])
      .join(', ');
  }
  return `${String(selected.length)} severities`;
}
